// Copyright (c) StrangeBee 2024

import { Pie, PieConfig } from '@ant-design/charts';
import useListQuery from '@Hooks/useListQuery';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useListAllFields } from '@Queries/describeQueries';
import client from '@Client';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import {
    CustomizeOutput,
    DonutWidgetType,
    WidgetOptionsOutput,
    WidgetOutput,
    ObjectTypeV1,
    CustomStatusOutput,
    DescribeFieldAttribute,
    UserOutput,
    ChartOutput,
} from 'thehive-sdk';
import { colorFormat, filterOnClick, parsePeriod } from './utils';
import RouteBuilder from '@Services/RouteBuilder';
import { useDashboard } from '@Hooks/useDashboard';
import useStorageSearch from '@Hooks/useStorageSearch';
import { SearchScopeType } from '@Types';
import { computeListSelector } from '@Organisms/CustomStatus/selector';
import { ActionWidget } from './ActionWidget';
import { downloadFile } from '@Helpers/download';
import { slugify } from '@Helpers/string';
import { RandomAntColors } from '@Constants/colors';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import _ from 'lodash';
import { useLocalMessage } from '@Helpers/locale';
import { Space } from 'antd';
import { useListAllUsers } from '@Queries/userQueries';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { baseUrl } from '../../../index';
import { UseQueryResult } from 'react-query';

import './Chart.scss';

interface DonutChartOutput {
    name: string;
    value: number;
    color?: string;
    key?: string;
    [key: string]: any;
}

interface DonutChartFormated {
    fields: DonutChartOutput[];
    total: number;
}

const prepareChartData = (
    donutData: ChartOutput[] | undefined,
    customize: CustomizeOutput[],
    describeField?: DescribeFieldAttribute,
    userList?: UserOutput[],
): DonutChartFormated => {
    const fields: DonutChartOutput[] =
        donutData?.reduce((acc, { _key, count }: any) => {
            const customizeIndex = customize.findIndex(({ name }) => name === _key);

            if (!count) {
                return acc;
            }

            const index =
                describeField?.type === 'user'
                    ? userList?.findIndex(({ login }) => login === _key)
                    : customizeIndex !== -1
                    ? customizeIndex
                    : describeField?.values.findIndex((value) => `${value}` === `${_key}`);

            const name =
                describeField?.type === 'user'
                    ? userList?.find(({ login }) => login === _key + '')?.name || _key
                    : index !== undefined && index !== -1 && index < customize.length
                    ? customize[index].label || ''
                    : _key === '_other'
                    ? 'others'
                    : _key + '';

            const title = index !== undefined && index !== -1 && index < customize.length ? customize[index].name : '';

            return [
                ...acc,
                {
                    name,
                    title,
                    value: count,
                    login: describeField?.type === 'user' ? _key + '' : undefined,
                    color:
                        index !== undefined && index !== -1 && index < customize.length
                            ? customize[index].color || ''
                            : '',
                },
            ];
        }, [] as DonutChartOutput[]) || [];
    const total = fields.find((item) => item.name === '_total')?.value || 0;
    if (!!total) {
        fields.splice(
            fields.findIndex((item) => item.name === '_total'),
            1,
        );
    }
    return { fields, total };
};

interface DonutWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput[]>;
    hasDownload?: boolean;
    onClick?: (value: any) => void;
}

export const DonutWidget = React.memo(
    ({ widget, query, hasDownload = true, onClick }: DonutWidgetProps): ReactElement => {
        const i = useLocalMessage();

        const [chart, setChart] = useState<any>();

        const options = widget.options as WidgetOptionsOutput & DonutWidgetType;

        const { data: describeData } = useListAllFields();
        const {
            entity,
            field = '',
            filters = [],
            periodDateField = '',
            customize = [],
            percentage = false,
        } = options || {};
        const { setSearchActiveScope, setSearchFilters, setSearchKeyword } = useStorageSearch();
        const { data: userList = [] } = useListAllUsers();
        const { dashboard } = useDashboard();
        const { definition } = dashboard || {};
        const { period = 'all', customPeriod } = definition || {};
        const { from, to } = parsePeriod(customPeriod, period);
        const periodFilters =
            period !== 'all'
                ? [
                      {
                          field: periodDateField,
                          type: 'date',
                          operator: 'custom',
                          values: [from, to],
                          describeAttribute: describeData?.[entity]?.attributes.find(
                              ({ name }) => name === periodDateField,
                          ) as any,
                      },
                  ]
                : [];

        const { data: customStatusList = [], refetch: refetchCustomStatusList } = useListQuery<CustomStatusOutput[]>({
            controller: client.CustomStatusController,
            queryParams: {
                selector: computeListSelector({ entity: entity as any }),
            },
            manual: true,
            fetchOnStart: false,
        });

        useEffect(() => {
            if (customStatusList.length) {
                return;
            }

            if (
                [ObjectTypeV1.CASE, ObjectTypeV1.ALERT, ObjectTypeV1.CASE_STATUS, ObjectTypeV1.ALERT_STATUS].includes(
                    entity,
                )
            ) {
                refetchCustomStatusList();
            }
        }, [customStatusList.length, entity, refetchCustomStatusList]);

        const customizeParsed = useMemo(() => {
            let index = 0;

            return customize?.map(({ color, ...field }) => {
                return { ...field, color: color || RandomAntColors[index++] };
            });
        }, [customize]);

        const describeField = useMemo(
            () => describeData?.[entity]?.attributes?.find(({ name }) => name === field),
            [describeData, entity, field],
        );

        if (query?.isLoading) {
            return (
                <div className="widget__empty">
                    <WidgetLoading />
                </div>
            );
        }

        if (query?.status === 'error') {
            return (
                <WidgetError
                    message={
                        !describeField?.aggregable && (
                            <Space direction="vertical" size={1}>
                                <span>
                                    {i('dashboard.widget.error.field', {
                                        field,
                                    })}
                                </span>

                                <span>{i('dashboard.widget.error.choose')}</span>
                            </Space>
                        )
                    }
                />
            );
        }

        if (!query?.data || _.isEmpty(query?.data)) {
            return (
                <div className="widget__empty">
                    <DisplayEmptyData label={i('dashboard.widget.empty')} />
                </div>
            );
        }

        const { fields: dataTreated, total } = prepareChartData(query?.data, customizeParsed, describeField, userList);

        const config: PieConfig = {
            data: dataTreated,

            angleField: 'value',
            colorField: 'name',

            color: customizeParsed?.length
                ? (datum) => customizeParsed?.find(({ label }) => label === datum?.name)?.color || ''
                : colorFormat({
                      field,
                      customStatusList,
                      entity,
                  }),

            legend: {
                flipPage: false,
                slidable: false,
                position: 'bottom',
                layout: 'vertical',
                itemHeight: 6,
                itemSpacing: 10,
                maxItemWidth: 500,
            },

            radius: 1,
            innerRadius: 0.6,

            label: {
                type: 'inner',
                offset: '-50%',
                content: (item) => {
                    const { value = 0 } = (item as DonutChartOutput) || {};

                    return `${percentage ? `${Math.round((value * 100) / total)}%` : value}`;
                },

                style: {
                    textAlign: 'center',
                    fontSize: 14,
                },
                autoRotate: false,
            },

            interactions: [
                {
                    type: 'element-active',
                    cfg: {
                        start: [
                            { trigger: 'element:mouseenter', action: 'cursor:pointer' },
                            { trigger: 'element:mouseleave', action: 'cursor:default' },
                        ],
                    },
                },
            ],

            // removed to manage TH5-2162 - perhaps it could be re-used when we'll switch to 5.3 with optimized query
            // statistic: {
            //     content: {
            //         content: `${total}`,
            //     },
            // },

            className: 'chart',
        };

        return (
            <div className="widget__display widget__donut">
                <Pie
                    {...config}
                    onReady={(plot) => {
                        setChart(plot);

                        plot.on('plot:click', (evt: any) => {
                            const { x, y } = evt;
                            const tooltipData = plot.chart.getTooltipItems({ x, y });

                            // login is for user type filter
                            // key is for XXX filter
                            // title is for severity, pap, tlp filter
                            // name is for custom label filter
                            const name = !!tooltipData.find(Boolean)?.data?.login
                                ? tooltipData.find(Boolean)?.data?.login
                                : tooltipData.find(Boolean)?.data?.key !== undefined
                                ? tooltipData.find(Boolean)?.data?.key
                                : !!tooltipData.find(Boolean)?.data?.title
                                ? tooltipData.find(Boolean)?.data?.title
                                : tooltipData.find(Boolean)?.data?.name;

                            if (name === undefined) {
                                return;
                            }

                            if (onClick) {
                                onClick(tooltipData.find(Boolean)?.data?.name);
                                return;
                            }

                            filterOnClick({
                                value: name,
                                entity,
                                field,
                                describeData,
                                onClick: (value, describeField) => {
                                    if (value !== 'others') {
                                        setSearchFilters(entity as SearchScopeType, [
                                            ...filters,
                                            ...periodFilters,
                                            {
                                                field,
                                                operator: 'any',
                                                values: [describeField?.type === 'boolean' ? JSON.parse(value) : value], // handle CF boolean filter (true or false were sent in string type)
                                                type: describeField?.type || 'string',
                                                describeAttribute: describeData?.[entity]?.attributes.find(
                                                    ({ name }) => name === field,
                                                ) as any,
                                            },
                                        ]);
                                        setSearchKeyword(entity as SearchScopeType, '');
                                        setSearchActiveScope(entity as SearchScopeType);
                                        window.open(
                                            baseUrl.substring(0, baseUrl.length - 1) +
                                                `${RouteBuilder.compute('search.default')}?fetch=true`,
                                            '_blank',
                                            'noopener,noreferrer',
                                        );
                                    }
                                },
                            });
                        });
                    }}
                />

                {hasDownload && (
                    <ActionWidget
                        onDownloadImage={() => chart.downloadImage(slugify(widget.options?.title || ''))}
                        onDownloadCSV={() => {
                            const tab = `field,count\n${dataTreated
                                ?.map(
                                    ({ name, value }) =>
                                        `${name},${percentage ? `${Math.round((value * 100) / total)}%` : value}`,
                                )
                                .join('\n')}`;

                            downloadFile(`${widget.options?.title || ''}.csv`, tab, 'text/csv');
                        }}
                    />
                )}
            </div>
        );
    },
);
